import React from "react";
import { Link } from "react-router-dom";
import {Calendar, User, ArrowRight, Clock} from 'lucide-react';

const BlogPage = () => {
  const blogPosts = [
    {
      title: "Understanding Drag Racing Insurance Cost in 2025",
      excerpt:
        "Comprehensive guide to drag racing insurance cost factors, typical premiums by class, and how to get the best rates for your high-value drag car.",
      category: "Insurance Costs",
      date: "January 15, 2025",
      readTime: "8 min read",
      image: "https://static.lumi.new/a9/a9d6ee4473ea1779d26a030091b94994.png",
      slug: "understanding-drag-racing-insurance-cost-2025",
    },
    {
      title: "5 Reasons Standard Auto Insurance Won't Cover Your Drag Car",
      excerpt:
        "Learn why regular car insurance excludes racing activities and why specialized drag racers insurance is essential for track protection.",
      category: "Coverage",
      date: "January 10, 2025",
      readTime: "6 min read",
      image: "https://static.lumi.new/0e/0e22873f7e56cbb7c097fe77cd965eef.png",
      slug: "standard-auto-insurance-wont-cover-drag-car",
    },
    {
      title: "Modified Vehicle Coverage: What Every Racer Should Know",
      excerpt:
        "How to properly insure modified drag racing vehicles with agreed value policies that protect your engine, suspension, and custom work.",
      category: "Modified Vehicles",
      date: "January 5, 2025",
      readTime: "7 min read",
      image: "https://static.lumi.new/38/38c11b411bc5c8ae57dbb07cf0364cc8.png",
      slug: "modified-vehicle-coverage-guide",
    },
    {
      title: "Insurance for Drag Racing Cars: Track vs. Full Coverage",
      excerpt:
        "Comparing track-only insurance policies with comprehensive drag race car insurance coverage for year-round protection.",
      category: "Policy Types",
      date: "December 28, 2024",
      readTime: "5 min read",
      image: "https://static.lumi.new/a9/a9d6ee4473ea1779d26a030091b94994.png",
      slug: "track-vs-full-coverage-comparison",
    },
    {
      title: "How to Lower Your Drag Racing Insurance Cost",
      excerpt:
        "Proven strategies to reduce premiums while maintaining comprehensive protection for your drag racing operation.",
      category: "Cost Savings",
      date: "December 20, 2024",
      readTime: "6 min read",
      image: "https://static.lumi.new/0e/0e22873f7e56cbb7c097fe77cd965eef.png",
      slug: "lower-drag-racing-insurance-cost",
    },
    {
      title: "NHRA vs IHRA: Insurance Coverage Differences",
      excerpt:
        "Understanding how drag racers insurance requirements vary between NHRA and IHRA sanctioned events.",
      category: "Sanctioning Bodies",
      date: "December 15, 2024",
      readTime: "7 min read",
      image: "https://static.lumi.new/38/38c11b411bc5c8ae57dbb07cf0364cc8.png",
      slug: "nhra-vs-ihra-insurance-differences",
    },
  ];

  return (
    <div className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#1a1a1a] border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="text-[#DC0000]">Drag Racing Insurance</span>{" "}
              Blog & Resources
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              Expert insights on drag racing insurance cost, coverage options,
              and protecting your high-value modified vehicles.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#1a1a1a] rounded-lg overflow-hidden border border-gray-800 hover:border-[#DC0000] transition-all">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="h-64 lg:h-full">
                <img
                  src={blogPosts[0].image}
                  alt={blogPosts[0].title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center space-x-4 text-sm text-gray-400 mb-4">
                  <span className="bg-[#DC0000] text-white px-3 py-1 rounded-full text-xs font-semibold">
                    Featured
                  </span>
                  <span className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {blogPosts[0].date}
                  </span>
                  <span className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    {blogPosts[0].readTime}
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  {blogPosts[0].title}
                </h2>
                <p className="text-gray-300 text-lg mb-6">
                  {blogPosts[0].excerpt}
                </p>
                <div>
                  <Link
                    to="/contact"
                    className="inline-flex items-center text-[#DC0000] font-semibold hover:underline"
                  >
                    Get Your Quote <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Latest Articles on{" "}
              <span className="text-[#DC0000]">Drag Racers Insurance</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Stay informed with expert advice on insurance for drag racing
              cars, cost-saving tips, and coverage strategies.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.slice(1).map((post, index) => (
              <article
                key={index}
                className="bg-black rounded-lg overflow-hidden border border-gray-800 hover:border-[#DC0000] transition-all transform hover:scale-105"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between text-sm text-gray-400 mb-3">
                    <span className="text-[#DC0000] font-semibold">
                      {post.category}
                    </span>
                    <span className="flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {post.readTime}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-400 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      {post.date}
                    </span>
                    <Link
                      to="/contact"
                      className="text-[#DC0000] font-semibold text-sm hover:underline flex items-center"
                    >
                      Read More <ArrowRight className="w-3 h-3 ml-1" />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-center">
            Browse by Category
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              "Insurance Costs",
              "Coverage Options",
              "Modified Vehicles",
              "Policy Types",
              "Cost Savings",
              "Safety & Regulations",
              "Racing Classes",
              "Claims Process",
            ].map((category, index) => (
              <Link
                key={index}
                to="/contact"
                className="bg-[#1a1a1a] p-4 rounded-lg border border-gray-800 hover:border-[#DC0000] transition-all text-center"
              >
                <span className="font-semibold">{category}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-[#DC0000]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white">
            Get Expert Drag Racing Insurance Advice
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Need personalized guidance on drag race car insurance? Our experts
            are ready to help you find the perfect coverage.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-black text-white px-8 py-4 rounded-md font-semibold text-lg hover:bg-gray-900 transition-all transform hover:scale-105"
          >
            Get Your Free Quote
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
